import { useEffect, useRef, useState } from "react";

import Image from "next/image";

import imageCompression from "browser-image-compression";

import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Upload,
  notification,
} from "antd";

import { createRedFlagForm, useCreateCustomerReport } from "@/hooks/useRedFlag";
import { getFileURL, uploadFile } from "@/hooks/useUploadFile";

import { CameraOutlined, DeleteOutlined } from "@ant-design/icons";

import { DEFAULT_COMPRESS_OPTIONS } from "@/utils/constants";
import useMilken from "@/hooks/useMilken";

export interface IDataRedFlag {
  issue: string;
  description: string;
  fileUpload?: File;
  referenceType: "EVENT" | "CIRCLE" | "CONNECTION" | "ENDORSEMENT" | "USER";
  referenceId: string;
  isAnonymous: boolean;
}

export interface IDataRedFlagInput extends IDataRedFlag {
  isAnonymous: boolean;
  isReportedToMyconnect: boolean;
  fileUploadInput: {
    file: File;
  };
}

type OptionalReferenceType = {
  referenceType?: IDataRedFlag["referenceType"];
  referenceId?: IDataRedFlag["referenceId"];
};

interface IPropsOnly {
  isOpen: boolean;
  title?: string;
  onSubmit?: (val: IDataRedFlag) => void;
  onClose: () => void;
  errorMessage?: string;
  successMessage?: string;
}

type IProps = IPropsOnly & OptionalReferenceType;

export default function RedFlagModal({
  isOpen,
  title,
  onSubmit,
  referenceType = "CONNECTION",
  referenceId = "",
  onClose = () => null,
  errorMessage,
  successMessage,
}: IProps) {
  const formRef = useRef<any>(null);
  const [api, contextHolder] = notification.useNotification();
  const [isLoadingRedFlag, isLoadingRedFlagSet] = useState(false);
  const [showResult, showResultSet] = useState(false);
  const [imgS3URL, imgS3URLSet] = useState<string | null>(null);
  const [fileImgRaw, fileImgRawSet] = useState<any>(null);
  const [isAnon, isAnonSet] = useState<boolean>(false);
  const [isReportedToMyconnect, setIsReportedToMyconnect] =
    useState<boolean>(false);
  const { isMilken } = useMilken();
  const defaultTitle = isMilken ? "Report" : "Report Red Flag";

  const {
    mutate: submitCustomerReport,
    isLoading,
    error,
  } = useCreateCustomerReport();

  async function onFinish(val: any) {
    const dataFile: File = val.fileUploadInput?.file?.originFileObj;

    if (onSubmit) {
      onSubmit({
        issue: val.issue,
        description: val.description,
        fileUpload: val.fileUploadInput?.file,
        referenceType,
        referenceId,
        isAnonymous: isAnon,
      });
    }

    isLoadingRedFlagSet(true);

    try {
      let payload: any = {
        issue: val.issue,
        description: val.description,
        referenceType,
        referenceId,
        isAnonymous: isAnon,
      };

      let compressedFile: File | undefined;
      if (fileImgRaw) {
        compressedFile = await imageCompression(
          dataFile,
          DEFAULT_COMPRESS_OPTIONS,
        );
        payload.fileUpload = compressedFile;
      }

      // Upload file if needed
      let attachmentKey: string | null = null;
      if (compressedFile) {
        const key = `redflag/${referenceType}/${Date.now()}-${compressedFile.name}`;
        await uploadFile({
          key,
          file: compressedFile,
          compressionEnabled: false, // Already compressed
          onStart: () => isLoadingRedFlagSet(true),
          onFinish: (uploadedKey: any) => console.log("Upload success:", uploadedKey),
          onError: (err: any) => {
            console.error("Upload failed:", err);
            api.error({
              message: "Upload Failed",
              description: "Unable to upload the image. Please try again.",
            });
          },
        });
        attachmentKey = key;
      }

      if (isReportedToMyconnect) {
        let urlImg;
        if (attachmentKey) {
          urlImg = await getFileURL(attachmentKey);
          imgS3URLSet(urlImg);
        }

        await submitCustomerReport({
          title: payload.issue,
          description: payload.description,
          attachmentUrl: urlImg ?? undefined,
          isAnonymous: isAnon,
        });

        api.success({
          message: "Red Flag Reported",
          description:
            successMessage || "You have successfully reported this matter.",
          onClose
        });

      } else {
        const keyImg = await createRedFlagForm(payload);
        const urlImg = keyImg ? await getFileURL(keyImg as string) : null;
        imgS3URLSet(urlImg);
      }

      

      if (!isReportedToMyconnect) {
        api.success({
          message: "Red Flag Reported",
          description:
            successMessage || "You have successfully reported this matter.",
        });

        showResultSet(true);
      }
    } catch (error) {
      api.error({
        message: "Error",
        description: errorMessage,
      });
    } finally {
      isLoadingRedFlagSet(false);
    }
  }

  useEffect(() => {
    if (!isOpen) {
      formRef.current?.resetFields();
      showResultSet(false);
      fileImgRawSet(null);
    }
  }, [isOpen]);

  return (
    <>
      {contextHolder}
      <Modal
        open={isOpen}
        onCancel={onClose}
        title={title || defaultTitle}
        footer={
          !showResult && (
            <Button
              loading={isLoadingRedFlag}
              onClick={() => formRef.current?.submit()}
              type="primary"
            >
              <span>
                Submit
                <CameraOutlined className="ml-2" />
              </span>
            </Button>
          )
        }
      >
        <div className="py-4">
          {!showResult && (
            <Form
              ref={formRef}
              onFinish={onFinish}
              layout="vertical"
              autoComplete="off"
              initialValues={{ remember: true }}
            >
              <Form.Item
                label="Issue"
                name="issue"
                rules={[
                  { required: true, message: "Issue shouldn't be empty" },
                ]}
              >
                <Input.TextArea placeholder="Write your issue here" />
              </Form.Item>
              <Form.Item
                label="Description"
                name="description"
                rules={[
                  { required: true, message: "Description shouldn't be empty" },
                ]}
              >
                <Input.TextArea placeholder="Write your description here" />
              </Form.Item>
              <Form.Item
                label="Screenshot"
                name="fileUploadInput"
                // rules={[{ required: true, message: "Description shouldn't be empty" }]}
              >
                {!fileImgRaw && (
                  <Upload
                    multiple={false}
                    accept="image/*"
                    maxCount={1}
                    fileList={[]}
                    onChange={(e) => fileImgRawSet(e)}
                  >
                    <Button>Click here to upload</Button>
                  </Upload>
                )}

                {fileImgRaw && (
                  <div className="flex items-center gap-2">
                    <p>{fileImgRaw?.file?.name}</p>
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        fileImgRawSet(null);
                      }}
                    >
                      <DeleteOutlined classID="text-red-600" />
                    </div>
                  </div>
                )}
              </Form.Item>

              <div className="flex items-center gap-2">
                <Checkbox
                  onChange={(e) => isAnonSet((prev) => !prev)}
                  checked={isAnon}
                >
                  Report Anonymously
                </Checkbox>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  onChange={(e) => setIsReportedToMyconnect((prev) => !prev)}
                  checked={isReportedToMyconnect}
                >
                  Report to MyConnect
                </Checkbox>
              </div>
            </Form>
          )}

          {showResult && (
            <div>
              <div className="mb-4">
                <p className="font-bold">Description</p>
                <p>{formRef.current?.getFieldsValue()?.description}</p>
              </div>

              <div>
                <p className="font-bold">Screenshot</p>
                {imgS3URL && (
                  <div className="w-full h-52 flex justify-center">
                    <Image
                      alt="redflag-screenshot"
                      src={imgS3URL}
                      className="object-cover"
                      width={300}
                      height={300}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
